<template>
    <v-container fluid class="main-container d-flex flex-column pa-4">
        <v-card class="d-flex flex-column flex-grow-1">
            <v-card-title class="pa-4 d-flex align-center justify-center" color="primary">
                <v-icon :icon="icons.mdiAccountGroupOutline" class="ma-2" color="primary" />
                <span class="font-weight-bold ma-2 text-primary">{{ $t('menu.customers') }}</span>
                <v-spacer />
                <v-btn class="align-center mr-2" color="secondary" density="default" rounded="rounded-md"
                    @click="createCustomer" :prepend-icon="icons.mdiPlus">{{ $t('action.newCustomer') }}</v-btn>
            </v-card-title>


            <v-container fluid class="pa-4 fill-height d-flex flex-column flex-grow-1">
                <AgGrid class="flex-grow-1" ref="customerGridRef" :fetchUrl="fetchUrl" :columnDefs="columnDefs"
                :gridParams="gridParams" :refresh="customerRefresh" @click-edit="onEditCustomer" :domLayout="domLayout" />
            </v-container>
        </v-card>

        <v-dialog v-model="customer.dialogVisible" width="560px" min-width="560px">
            <v-card class="w-100 overflow-visible pt-16">
                <v-form @submit.prevent v-model="vformCustomer" class="pt-14">
                    <v-card-item class="w-75 ma-auto overflow-visible">

                        <v-avatar size="205" color="surface" class="position-absolute dialog-picture elevation-3" border="border-color xl">
                            <v-img v-if="customer.pictureUrl" :src="customer.pictureUrl" alt="Cutomer Logo" />
                            <v-icon v-else  :icon="icons.mdiAccountMultipleOutline" color="primary" size="120" />
                        </v-avatar>

                        <v-btn v-if="false" :icon="icons.mdiCamera" size="small" color="primary" @click="onEditPicture" />

                        <v-text-field v-if="false" v-model="customer.pictureUrl" :label="$t('field.picture')"
                            :prepend-inner-icon="icons.mdiWeb" variant="outlined" density="comfortable"/>

                        <v-text-field :label="$t('field.label')"  class="pt-2" color="primary" icon-color="primary" v-model="customer.name"
                            :prepend-inner-icon="icons.mdiFormatText" variant="outlined" required
                            :rules="[rules.required]" maxlength="25" counter="25" />
                    </v-card-item>

                    <v-divider />

                    <v-card-text>
                        <v-container>
                            <v-text-field :label="$t('field.reference')" color="primary" :prepend-inner-icon="icons.mdiCodeBrackets" icon-color="primary"
                            v-model="customer.reference" variant="outlined"
                            :disabled="customer.editMode" :readonly="customer.editMode"
                            :rules="[rules.required]" />

                            <v-text-field :label="$t('field.alias')" color="primary" :prepend-inner-icon="icons.mdiAlphaABoxOutline"
                            icon-color="primary" v-model="customer.alias" variant="outlined" />

                            <v-text-field v-if="customer.editMode" :label="$t('field.database')" color="primary" :prepend-inner-icon="icons.mdiDatabase"
                                icon-color="primary" v-model="customer.database" variant="outlined"
                                :disabled="customer.editMode" :readonly="customer.editMode"/>

                            <v-select :label="$t('field.country')" color="primary" :prepend-inner-icon="icons.mdiWeb"
                            icon-color="primary" v-model="customer.country" variant="outlined"
                            :items="countries" item-title="name" item-value="code"
                            :rules="[rules.required]"/>

                            <v-select :label="$t('field.distributor')" color="primary" :prepend-inner-icon="icons.mdiWeb"
                            icon-color="primary" v-model="customer.distributor" variant="outlined"
                            :items="distributors" item-title="name" item-value="id"/>

                            <v-row>
                                <v-col cols="12" sm="4">
                                    <v-checkbox color="secondary" v-model="customer.demo" :label="$t('field.demo')" density="compact"
                                        hide-details />
                                </v-col>

                                <v-col cols="12" sm="4">
                                    <v-checkbox color="secondary" v-model="customer.ediEnabled" :label="$t('field.ediEnabled')"
                                        density="compact" hide-details :model-value="true" />
                                </v-col>

                                <v-col cols="12" sm="4">
                                    <v-checkbox color="secondary" v-model="customer.active" :label="$t('field.active')" density="compact"
                                        hide-details :model-value="true" />
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>

                    <v-divider />

                    <v-card-actions  class="justify-end">
                        <v-btn color="error" variant="text" @click="onCloseDialog">
                            {{ $t('action.cancel') }}
                        </v-btn>
                        <v-btn color="primary" variant="text" type="submit" @click="saveCustomer"
                            :disabled="!vformCustomer">
                            {{ $t('action.ok') }}
                        </v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import AgGrid from '@/components/AgGrid.vue';
import { toast } from 'vue3-toastify';
import { inject, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useLogStore } from '@/stores/logStore';
import { useUserStore } from '@/stores/userStore';
import {
    mdiPlus,
    mdiAccountMultipleOutline,
    mdiAccountGroupOutline,
    mdiFormatText,
    mdiCamera,
    mdiWeb,
    mdiCodeBrackets,
    mdiAlphaABoxOutline,
    mdiDatabase,
} from '@mdi/js';

import CountriesJson from '@/components/json/countries.json';

const urlWebAPI = inject('urlWebAPI');
const translate = inject('translate');

const route = useRoute();
const logStore = useLogStore();
const userStore = useUserStore();

const icons = {
    mdiPlus,
    mdiAccountMultipleOutline,
    mdiAccountGroupOutline,
    mdiFormatText,
    mdiCamera,
    mdiWeb,
    mdiCodeBrackets,
    mdiAlphaABoxOutline,
    mdiDatabase,
};

const fetchUrl = 'GetCustomers';
const columnDefs = [
    { field: 'edit', headerName: '', type: 'editAction', cellDataType: 'text' , filter: false, sortable: false, resizable: true, suppressHeaderMenuButton: true},
    { field: 'manage', headerName: '', type: 'manageAction', cellDataType: 'text', filter: false, sortable: false, resizable: true, suppressHeaderMenuButton: true },
    { field: 'users', headerName: '', type: 'userAction', cellDataType: 'text', filter: false, sortable: false, resizable: true, suppressHeaderMenuButton: true },
    { field: 'name', headerName: translate('name', 'column'), cellDataType: 'text' },
    { field: 'reference', headerName: translate('reference', 'column'), cellDataType: 'text' },
    { field: 'alias', headerName: translate('alias', 'column'), cellDataType: 'text' },
    { field: 'distributorName', headerName: translate('distributor', 'column'), cellDataType: 'text' },
    { field: 'ediEnabled', headerName: translate('ediEnabled', 'column'), type: 'boolean', editable: false, cellDataType: 'boolean' },
    { field: 'active', headerName: translate('active', 'column'), type: 'boolean', editable: false, cellDataType: 'boolean' },
    { field: 'demo', headerName: translate('demo', 'column'), type: 'boolean', editable: false, cellDataType: 'boolean' },
];
const gridParams = {
    token: userStore.getUser().token,
    customerReference: (route.params.customerReference != 'undefined') ? route.params.customerReference : null,
    distributorId: userStore.getUser().distributorId,
};

const countries = CountriesJson;
const customerRefresh = ref(false);
const customer = reactive({
    name: '',
    alias: '',
    reference: '',
    database: '',
    distributor: null,
    refresh: false,
    pictureUrl: null,
    active: true,
    ediEnabled: true,
    demo: false,
    country: null,
    dialogVisible: false,
    createMode: false,
    editMode: false,
});

const distributors = ref([]);
const domLayout = 'normal';

const vformCustomer = ref(false);
const rules = {
    required: (value) => !!value || "required"
};

//#region created()
    getDistributors();
//endregion


function onEditPicture() {
    //TODO;
}

function createCustomer() {
    for (var key in customer)
        delete customer[key];

    customer.createMode = true;
    customer.editMode = false;
    customer.dialogVisible = true;
}

//to remove
function onEditCustomer(customerData) {
    getCustomerDetail(customerData);
}

function editCustomer(customerData) {
    customer.editMode = true;
    customer.createMode = false;

    customer.name = customerData.name;
    customer.alias = customerData.alias;
    customer.reference = customerData.reference;
    customer.database = customerData.dbConnectionString;
    customer.distributor = customerData.distributorId;
    customer.demo = customerData.demo;
    customer.pictureUrl = customerData.logo;
    customer.active = customerData.active;
    customer.ediEnabled = customerData.ediEnabled;
    customer.country = customerData.secondColor;

    customer.dialogVisible = true;
}

function saveCustomer() {
    // UPDATE CUSTOMER
    let data = {
        token: userStore.getUser().token,
        customerReference: customer.reference.toUpperCase(),
        customerInfo: {
            name: customer.name.toUpperCase(),
            reference: customer.reference.toUpperCase(),
            distributorId: customer.distributorId,
            logo: customer.pictureUrl,
            alias: customer.alias,
            secondColor: customer.country,
            demo: customer.demo,
            ediEnabled: customer.ediEnabled,
            active: customer.active,
        },
        distributorId: userStore.getUser().distributorId,
        isCreation: customer.createMode,
    };

    $.postData(urlWebAPI + 'SaveCustomerDetail', data)
        .then((data) => {
            toast.success(translate('customerInfoWellSaved', 'toast'))

            customerRefresh.value = true;
            customer.createMode = true;
            customer.dialogVisible = false;
            // RESET REFRESH
            setTimeout(() => {
                customerRefresh.value = false;
            }, 1000);
        })
        .catch(error => {
            console.log(error);
            logStore.log(error, 'error');
            toast.error('Error during executing SaveCustomerDetail data.');
        })

}

function onCloseDialog() {
    customer.dialogVisible = false;
}

function setDialogLogo(customerData = null) {

}

function hideDialogLogo() {
}

function showDialogLogo() {
}

// #region DATA

async function getCustomerDetail(customerData) {
    let data = {
        token: userStore.getUser().token,
        customerReference: customerData.reference,
    };

    $.getData(urlWebAPI + 'GetCustomerDetail', data)
        .then((data) => {
            editCustomer(data);
        })
        .catch(error => {
            logStore.log(error, 'error');
            toast.error('Error during executing GetCustomerDetail data.');
        });
}

function getDistributors() {
    let data = {
        token: userStore.getUser().token,
    };

    $.getData(urlWebAPI + 'GetDistributors', data)
        .then((data) => {
            data.unshift({ id: 0, name: translate('none', 'field') });
            distributors.value = data;
        })
        .catch(error => {
            logStore.log(error, 'error');
            toast.error('Error during executing GetDistributors data.');
        });
}

// #endregion
</script>
